/**
 * API calls of the shop file's period-backfill tab, on the generated `InternalAdminApi`.
 *
 * Both calls carry the shop's access grant (`shopGrant`); a refusal for want of one is recognised
 * by `isShopAccessRequired` in the caller, which then asks for a reason again.
 */

import axios from 'axios';
import type {
  PeriodBackfillExecuteRequestDto,
  PeriodBackfillExecuteResultDto,
  PeriodBackfillPreviewDto,
} from '../../../../libs/api-client/src/generated/models';
import { opsClient, shopGrant } from './ops-api';

// Re-exported from the generated client rather than restated here — see `support-api.ts`.
export type {
  PeriodBackfillCandidateDto as PeriodBackfillCandidate,
  PeriodBackfillExclusionGroupDto as PeriodBackfillExclusionGroup,
  PeriodBackfillExecuteResultDto as PeriodBackfillExecuteResult,
  PeriodBackfillOrderResultDto as PeriodBackfillOrderResult,
  PeriodBackfillPreviewDto as PeriodBackfillPreview,
  PeriodBackfillWindowDto as PeriodBackfillWindow,
} from '../../../../libs/api-client/src/generated/models';

/**
 * The API's `message` of a failed call, or null — for matching the period-backfill refusal
 * codes (`PERIOD_BACKFILL_PREVIEW_EXPIRED`, …) without asserting the error's shape.
 *
 * @param error - Whatever the call threw.
 */
export function periodBackfillErrorMessage(error: unknown): string | null {
  if (!axios.isAxiosError<{ message?: unknown }>(error)) return null;
  const message = error.response?.data?.message;
  return typeof message === 'string' ? message : null;
}

/** The dry run for a period (ISO bounds, inclusive). Writes only its audit row. */
export async function previewPeriodBackfill(
  shopId: string,
  period: { readonly from: string; readonly to: string },
): Promise<PeriodBackfillPreviewDto> {
  const { data } = await opsClient().previewOpsPeriodBackfill({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
    periodBackfillPreviewRequestDto: { from: period.from, to: period.to },
  });
  return data;
}

/** Mints invoices for previewed orders. Each one is re-checked by the API before it is minted. */
export async function executePeriodBackfill(
  shopId: string,
  body: PeriodBackfillExecuteRequestDto,
): Promise<PeriodBackfillExecuteResultDto> {
  const { data } = await opsClient().executeOpsPeriodBackfill({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
    periodBackfillExecuteRequestDto: body,
  });
  return data;
}
